import {
  convertBooleanToNumber,
  convertTimestampToDate,
  getExercisedData,
  getHourFromTimestamp,
  getTime,
  convertForceSellEvent,
  convertSmartOrderConfigType,
  convertSecuritiesKind,
} from 'utils/securities/formatter';

import type {
  PortfolioState,
  PortfolioStateItem,
} from 'global/Securities/types/store/portfolio';
import {
  OrderStateItem,
  OrderAction,
  iOrderSide,
} from 'global/Securities/types/store/order';

export const convertSideToAction = (side: iOrderSide): OrderAction =>
  side === iOrderSide.BUY ? OrderAction.Buy : OrderAction.Sell;

const portfolioSummary = (portfolio: PortfolioSummary): PortfolioState => {
  const { trading, amount, profitLoss, gain, equity, debt } = portfolio ?? {};
  return {
    tradingbalance: trading?.balance,
    amount_invested: amount?.invested,
    amount_allocated: amount?.allocated,
    amount_creditlimit: amount?.creditLimit,
    unrealised_profitloss: profitLoss?.unrealised,
    realised_profitloss: profitLoss?.realised,
    profitloss: profitLoss?.net,
    gain,
    equity,
    debt_ratio: debt?.ratio,
    debt_total: debt?.total,
    debt_market_value: debt?.marketValue,
  };
};

const portfolioItem = (data: PortfolioDetail): PortfolioStateItem => {
  const { asset, company, exercise, flags, price, qty, symbol, isSharia } =
    data ?? {};

  return {
    allow_order: convertBooleanToNumber(flags?.allowOrder),
    amount_invested: asset?.amountInvested,
    available_lot: qty?.available.lot,
    available_share: qty?.available.share,
    balance_lot: qty?.balance.lot,
    balance_share: qty?.balance.share,
    company_name: company?.name,
    exercise_data: getExercisedData(exercise),
    exercise_type: exercise?.type,
    have_smartorder: flags?.haveSmartOrder,
    price_average: price?.average.price,
    price_average_fee: price?.average.fee,
    price_latest: price?.latest,
    stock_on_hand: qty?.stockOnHand,
    symbol,
    total: asset?.amountInvested,
    unrealised_gain: asset?.unrealised.gain,
    unrealised_marketvalue: asset?.unrealised.marketValue,
    unrealised_profitloss: asset?.unrealised.profitLoss,
    can_buy: flags?.canBuy,
    can_sell: flags?.canSell,
    is_sharia: isSharia,
  };
};

const portfolioList = (portfolio: PortfolioList): PortfolioState => {
  const { summary, smartOrderInfo, results, dayTrade } = portfolio ?? {};

  return {
    tradingbalance: summary?.trading?.balance,
    amount_invested: summary?.amount?.invested,
    amount_allocated: summary?.amount?.allocated,
    amount_creditlimit: summary?.amount?.creditLimit,
    unrealised_profitloss: summary?.profitLoss?.unrealised,
    realised_profitloss: summary?.profitLoss?.realised,
    profitloss: summary?.profitLoss?.net,
    gain: summary?.gain,
    equity: summary?.equity,
    show_create_smartorder: smartOrderInfo?.canCreate,
    auto_order_info: smartOrderInfo?.info,
    result: results?.map((result) => portfolioItem(result)),
    day_trade: dayTrade?.map((result) => portfolioItem(result)),
    debt_ratio: summary?.debt?.ratio,
    debt_total: summary?.debt?.total,
    debt_market_value: summary?.debt?.marketValue,
  };
};

const orderDetail = (order: OrderDetail): OrderStateItem => {
  const {
    action,
    amount,
    dayTrade,
    gtc,
    marketOrderId,
    message,
    orderId,
    price,
    qty,
    side,
    smartOrder,
    statusText,
    symbol,
    time,
    isSharia,
    securitiesKind,
  } = order ?? {};

  const securities_kind = convertSecuritiesKind(securitiesKind);
  const isBond = securities_kind === 'SECURITIES_KIND_BOND';

  return {
    action: convertSideToAction(side),
    amend_action: action.canAmend,
    amount: amount?.amount,
    amount_fee: amount?.fee.amount,
    amount_invested: amount?.invested,
    amount_invested_fee: amount?.fee.invested,
    amount_matched: isBond ? amount?.fee?.invested : amount?.matched,
    amount_matched_broker_fee: amount?.fee.matched.broker,
    amount_matched_exchange_fee: amount?.fee.matched.exchange,
    amount_matched_fee: amount?.fee.matched.amount,
    amount_matched_total: amount?.matchedTotal,
    amount_open: amount?.open,
    amount_open_fee: amount?.fee.open,
    cancel_action: action.canCancel,
    config_type: convertSmartOrderConfigType(
      smartOrder?.configType?.toString(),
    ),
    gtc: convertBooleanToNumber(gtc?.isGtc),
    label: smartOrder?.label,
    market_orderid: marketOrderId,
    message,
    order_done: qty?.lotDone,
    order_open: qty?.lotOpen,
    order_total: qty?.lotTotal,
    orderid: orderId,
    price: price?.order,
    price_average: price?.average.price,
    price_average_fee: price?.average.fee,
    price_order: price?.order,
    smart_orderid: smartOrder?.orderId,
    status: statusText,
    symbol,
    securities_kind,
    time: getHourFromTimestamp(getTime(time?.open)),
    time_match: convertTimestampToDate(getTime(time?.match)),
    time_open: convertTimestampToDate(getTime(time?.open)),
    trigger_price: smartOrder?.triggerPrice,
    day_trade_is_day_trade: dayTrade?.isDayTrade,
    day_trade_force_sell_event: convertForceSellEvent(dayTrade?.forceSellEvent),
    day_trade_message: dayTrade?.message,
    is_sharia: isSharia,
  };
};

const orderList = (response: OrderList): OrderStateItem[] => {
  const { orders } = response ?? {};

  return orders?.map((order) => orderDetail(order));
};

export default {
  portfolioSummary,
  portfolioItem,
  portfolioList,
  orderDetail,
  orderList,
};
